"use client";

// ArtifactTabNav — the collapsed navigator for the full-page expanded view. Follows
// the OsNavigator law: the active document sits at the FAR LEFT, the rest trail
// faded to the right and wrap around ("cycling"), and a drag/swipe (or tap) switches
// document. A frosted collapse toggle stays fixed at the left; a reading-progress
// underline fills beneath the active tab as you scroll.
//
// Seating is done by RENDER ORDER (active first) + framer `layout` for the slide —
// NOT a CSS transform/translate/left/margin on a strip, every one of which was
// silently overridden here by the higher-cascade `.section-slide` ancestor animation.
// framer drives each button's own transform (like the hover nudge already does), so
// the reorder animation is immune to that.

import { useCallback, useEffect, useRef, useState } from "react";
import { type OsNode } from "../../../lib/os/tree";
import ExpandGlyph from "./ExpandGlyph";

export default function ArtifactTabNav({
  pages,
  index,
  onIndex,
  onCollapse,
}: {
  pages: OsNode[];
  index: number;
  onIndex: (i: number) => void;
  onCollapse?: () => void;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const readRef = useRef<HTMLSpanElement>(null);
  const drag = useRef<{ x0: number; last: number; lastT: number; vx: number; moved: boolean; pid: number } | null>(null);
  const didDrag = useRef(false);
  const [dragging, setDragging] = useState(false);
  const n = pages.length;
  const activeIdx = Math.max(0, Math.min(index, n - 1));

  // live reading progress → the underline fill beneath the active tab.
  useEffect(() => {
    const on = (e: Event) => readRef.current?.style.setProperty("--art-read", String(Math.max(0, Math.min(1, (e as CustomEvent<number>).detail))));
    window.addEventListener("innernet:art-read", on as EventListener);
    return () => window.removeEventListener("innernet:art-read", on as EventListener);
  }, []);
  useEffect(() => { readRef.current?.style.setProperty("--art-read", "0"); }, [activeIdx]);

  const step = useCallback((dir: 1 | -1) => { if (n >= 2) onIndex(((activeIdx + dir) % n + n) % n); }, [activeIdx, n, onIndex]);

  const onDown = (e: React.PointerEvent) => { drag.current = { x0: e.clientX, last: e.clientX, lastT: e.timeStamp, vx: 0, moved: false, pid: e.pointerId }; didDrag.current = false; };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x0;
    if (!d.moved && Math.abs(dx) > 4) { d.moved = true; didDrag.current = true; setDragging(true); try { railRef.current?.setPointerCapture(d.pid); } catch { /* ignore */ } }
    if (!d.moved) return;
    const dt = e.timeStamp - d.lastT;
    if (dt > 0) d.vx = (e.clientX - d.last) / dt;
    d.last = e.clientX;
    d.lastT = e.timeStamp;
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    if (!d?.moved) return;
    const dx = d.last - d.x0;
    if ((Math.abs(dx) > 50 || Math.abs(d.vx) > 0.4) && n > 1) step(dx < 0 ? 1 : -1); // one at a time
    setTimeout(() => { didDrag.current = false; }, 0);
  };

  // display order — active FIRST, the rest cycling after it, so the active word
  // always seats at the far-left edge (framer `layout` slides the reorder).
  const order = Array.from({ length: n }, (_, k) => (activeIdx + k) % n);

  return (
    <div className="art-tabnav" data-noswipe>
      {onCollapse && (
        <button className="art-collapse" aria-label="collapse to cards" onClick={onCollapse}>
          <ExpandGlyph />
        </button>
      )}
      <div
        ref={railRef}
        className="art-tabnav__rail os-sib-rail"
        // law_nav_proximity — near the rail the tabs come up to meet the pointer.
        data-hl-near="28"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
        style={{ cursor: dragging ? "grabbing" : "grab", touchAction: "pan-y" }}
      >
        <div className="art-tabnav__strip">
          {order.map((idx) => {
            const p = pages[idx];
            const on = idx === activeIdx;
            return (
              <button
                key={p.key}
                // colour + opacity are CSS's (.art-tab / .os-sib-word): the one
                // navigator grammar, lit by the global HoverField tracker.
                className="art-tab os-sib-word"
                data-on={on}
                data-active={on ? "" : undefined}
                aria-selected={on}
                role="tab"
                onClick={() => { if (didDrag.current) return; onIndex(idx); }}
              >
                {p.name}
                {on && <span ref={readRef} className="art-tab__read" aria-hidden />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
